import { Chip, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import { makeStyles } from "@mui/styles";
import React, { memo, useMemo } from "react";
import { getTypeColor } from "./typeColors";

const useStyles = makeStyles({
  card: {
    margin: "20px 26px",
    borderRadius: "12px",
    boxShadow: "3px 4px 7px 0px #0000000f",
    border: "1px solid #d3d3d34a",
    display: "flex",
    flexDirection: "row",
    padding: "0px 8px",
    cursor: "pointer",
    alignItems: "center",
    gap: "12px",
    "&:hover": {
      backgroundColor: "#f4f4f47d",
    },
    position: "relative",
    minHeight: "100px",
    background: "white",
  },
  sponsoredFeaturedCard: {
    border: "1.5px solid #7b5ac8",
    boxShadow: "4px 8px 18px #2f24522b",
    backgroundColor: "#f6f1ff",
    minHeight: "140px",
    padding: "8px 12px",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f1eaff",
    },
  },
  sponsoredAccentCard: {
    border: "1.5px solid #7b5ac8",
    boxShadow: "3px 4px 9px 0px #4d347a1a",
  },
  sponsoredCornerIconWrapper: {
    position: "absolute",
    top: "10px",
    right: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    cursor: "default",
    zIndex: 2,
  },
  sponsoredCornerIcon: {
    fontSize: "16px !important",
    color: "#7b5ac8",
    filter: "drop-shadow(0 1px 1px #ffffff)",
  },
  imagePlaceholder: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imageContainer: {
    width: "82px",
    minWidth: "82px",
    height: "82px",
    borderRadius: "10px",
    backgroundColor: "#ffcc87",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: "6px",
  },
  sponsoredImageContainer: {
    width: "112px",
    minWidth: "112px",
    height: "88px",
    borderRadius: "12px",
    backgroundColor: "#f6eaf9",
    boxShadow: "0 1px 4px #4b36641f",
  },
  chip: {
    marginRight: "4px",
    marginBottom: "3px",
    fontSize: "10px !important",
    height: "20px !important",
    "& .MuiChip-label": {
      padding: "0 7px",
    },
  },
  chipContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: "6px",
  },
  detailsContainer: {
    flex: 1,
    wordBreak: "break-word",
    minWidth: 0,
    margin: "8px 0",
  },
  sponsoredDescription: {
    marginTop: "8px !important",
    color: "#423099",
    fontSize: "13px !important",
    lineHeight: "1.35 !important",
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
  },
  placeholderDescription: {
    color: "#111111",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    alignSelf: "stretch",
    flexShrink: 0,
    paddingY: "4px",
  },
  chevronButton: {
    display: "inline-flex",
    borderRadius: "6px",
    padding: "3px",
    cursor: "pointer",
    color: "#757575",
    transition: "color 0.2s, background-color 0.2s",
    "&:hover": {
      color: "#b85c38",
      backgroundColor: "#b85c381a",
    },
  },
  linkButton: {
    position: "relative",
    display: "inline-flex",
    borderRadius: "6px",
    padding: "3px",
    cursor: "pointer",
    color: "#757575",
    transition: "color 0.2s, background-color 0.2s",
    "&:hover": {
      color: "#b85c38",
      backgroundColor: "#b85c381a",
    },
  },
  "@media (max-width: 520px)": {
    card: {
      margin: "14px 12px",
      gap: "9px",
      paddingLeft: "8px",
      paddingRight: "6px",
    },
    sponsoredStarWrapper: {
      left: "-10px",
    },
    sponsoredImageContainer: {
      width: "96px",
      minWidth: "96px",
      height: "76px",
    },
  },
});

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const domainFromUrl = (rawUrl) => {
  try {
    const url = rawUrl.startsWith("http") ? rawUrl : "https://" + rawUrl;
    return new URL(url).hostname.replace(/[^a-z0-9.-]/gi, "_");
  } catch {
    return null;
  }
};

const DestinationCard = (props) => {
  const {
    destination,
    googleMarker,
    map,
    sponsoredVariant,
    onCardClick,
  } = props;
  const { name, type, city, country, province, url, description } = destination;
  const screenshotDomain = useMemo(() => domainFromUrl(url), [url]);
  const sortedTypes = useMemo(() => [...(type ?? [])].sort(), [type]);
  const sponsoredDescription = useMemo(() => {
    if (typeof description === "string" && description.trim()) {
      return description.trim();
    }

    const destinationName = capitalize(city || name);
    return destinationName
      ? `Discover ${destinationName}: where ceramic culture comes into view.`
      : "";
  }, [city, description, name]);

  const handleHover = () => {
    window.google.maps.event.trigger(googleMarker, "mouseover", "card-hover");
  };

  const handleBlur = () => {
    window.google.maps.event.trigger(googleMarker, "mouseout");
  };

  const handleClick = () => {
    onCardClick?.();
    map.setCenter(googleMarker.getPosition());
    map.setZoom(15);
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      window.google.maps.event.trigger(googleMarker, "mouseover", true);
    });
    const params = new URLSearchParams(window.location.search);
    params.set("destinationId", destination.id);
    window.history.replaceState(null, "", "?" + params.toString());
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    const href = url.startsWith("http") ? url : "https://" + url;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const classes = useStyles();
  const effectiveSponsoredVariant =
    sponsoredVariant || (destination.sponsored ? "featured" : undefined);
  const isFeaturedSponsored = effectiveSponsoredVariant === "featured";
  const isPlaceholderSponsored =
    effectiveSponsoredVariant === "featuredPlaceholder";
  const isDescriptionOnlySponsored =
    effectiveSponsoredVariant === "descriptionOnly";
  const usesSponsoredFeatureLayout = isFeaturedSponsored;
  const isSponsoredAccent = effectiveSponsoredVariant === "accent";
  const displayedSponsoredDescription = isPlaceholderSponsored
    ? "Placeholder sponsored description for this destination."
    : isDescriptionOnlySponsored
      ? "Placeholder listing description for this destination."
      : sponsoredDescription;
  const cardClassName = [
    classes.card,
    usesSponsoredFeatureLayout && classes.sponsoredFeaturedCard,
    (isPlaceholderSponsored || isSponsoredAccent) &&
      classes.sponsoredAccentCard,
  ]
    .filter(Boolean)
    .join(" ");
  const imageContainerClassName = [
    classes.imageContainer,
    usesSponsoredFeatureLayout && classes.sponsoredImageContainer,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClassName}
      onMouseEnter={handleHover}
      onMouseLeave={handleBlur}
      onClick={handleClick}
    >
      {(isFeaturedSponsored ||
        isPlaceholderSponsored ||
        isSponsoredAccent ||
        effectiveSponsoredVariant === "iconOnly") && (
        <Tooltip
          title="This destination is a paid sponsor of MOCA-NY"
          placement="top"
        >
          <span
            className={classes.sponsoredCornerIconWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            <AutoAwesomeIcon className={classes.sponsoredCornerIcon} />
          </span>
        </Tooltip>
      )}
      {/* <div className={imageContainerClassName}>
        <img
          key={url}
          className={classes.imagePlaceholder}
          src={`/images/destination_screenshots/${screenshotDomain}.jpg`}
          // src="/images/placeholder_screenshot.png"
          alt=""
          onError={(e) => {
            if (e.currentTarget.dataset.fallback) {
              return;
            }
            e.currentTarget.dataset.fallback = "1";
            e.currentTarget.src = "/images/placeholder_screenshot.png";
            e.currentTarget.style.width = "100%";
            e.currentTarget.style.height = "100%";
            e.currentTarget.style.minWidth = "unset";
            e.currentTarget.style.borderRadius = "0";
            e.currentTarget.style.objectFit = "cover";
          }}
        />
      </div> */}

      <div className={classes.detailsContainer}>
        <Typography sx={{ fontSize: "15px", fontWeight: "bold" }}>
          {capitalize(name)}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.primary" }}>
          {capitalize(city)}, {`${province ? capitalize(province) + ", " : ""}`}
          {capitalize(country)}
        </Typography>
        <div className={classes.chipContainer}>
          {sortedTypes.map((type, index) => (
            <Chip
              style={{
                backgroundColor: getTypeColor(type),
                color: "#000000",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: "600",
              }}
              classes={{ root: classes.chip }}
              key={type + index}
              label={type}
            />
          ))}
        </div>
        {(usesSponsoredFeatureLayout ||
          isPlaceholderSponsored ||
          isDescriptionOnlySponsored) &&
          displayedSponsoredDescription && (
            <Typography
              className={`${classes.sponsoredDescription}${
                isPlaceholderSponsored || isDescriptionOnlySponsored
                  ? ` ${classes.placeholderDescription}`
                  : ""
              }`}
            >
              {displayedSponsoredDescription}
            </Typography>
          )}
      </div>

      <div className={classes.actions}>
        <Tooltip title="Go to website" placement="top">
          <div className={classes.chevronButton} onClick={handleChevronClick}>
            <ChevronRightIcon />
          </div>
        </Tooltip>
        <div style={{ position: "relative" }}>
          <Tooltip title="Go to URL" placement="bottom">
            <div className={classes.linkButton} onClick={handleChevronClick}>
              <LinkIcon style={{ fontSize: "20px" }} />
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default memo(DestinationCard);
